import {
  Box,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
  MenuItem,
  Select,
  Paper,
  Drawer,
  IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import FilterListIcon from '@mui/icons-material/FilterList'

import { format, parse } from 'date-fns'

import { Notification } from '@components/prereg_validation/notification'
import { useSessionContext } from '@providers/session'
import { i18WithDefault as td, i18WithParams as t } from '@shared/locale'
import { BookingCalendar } from 'components/booking_calendar/booking_calendar'
import { BookingCalendarLegend } from 'components/booking_calendar/booking_calendar_legend'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useClientLocationServiceType } from 'service-api/src/hooks/use_client_location_service_type'
import { BookingInvoiceRange, useBookingInvoices } from './use_booking_invoices'
import {
  ALL,
  BookingsView,
  SERVICE_TYPE_FILTER_OPTIONS,
  STATUS_FILTER_OPTIONS,
  useBookingsUrlState,
} from './use_bookings_url_state'
import { BookingList } from 'views/booking_list/booking_list'

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, month) => ({
  value: month,
  label: format(new Date(2000, month, 1), 'MMMM'),
}))

const getServiceTypeLabel = (serviceType: string): string => td(`calendar.serviceType.${serviceType}`) || serviceType

const toRange = (start: Date, end: Date): BookingInvoiceRange => ({
  start: format(start, 'yyyy-MM-dd'),
  end: format(end, 'yyyy-MM-dd'),
})

const Skelly = () => {
  return (
    <Stack data-testid={'loading'} sx={{ width: '100%' }} direction="column">
      <Skeleton height={40} />
      <Skeleton height={40} />
      <Skeleton height={40} />
      <Skeleton height={40} />
      <Skeleton height={40} />
      <Skeleton height={40} />
    </Stack>
  )
}

export const Bookings = () => {
  const sessionCtx = useSessionContext()
  const theme = useTheme()
  const isBelowLg = useMediaQuery(theme.breakpoints.down('lg'))
  const isBelowMd = useMediaQuery(theme.breakpoints.down('md'))

  const { accountName = '' } = useParams()
  const [filtersOpen, setFiltersOpen] = useState(false)

  const {
    view,
    status,
    serviceType,
    sortBy,
    sortOrder,
    rowsPerPage,
    month,
    setView,
    setStatus,
    setServiceType,
    setSort,
    setRowsPerPage,
    setMonth,
  } = useBookingsUrlState()

  const monthDate = useMemo(() => parse(month, 'yyyy-MM', new Date()), [month])

  // Unset until the calendar reports its visible grid on mount
  const [visibleRange, setVisibleRange] = useState<BookingInvoiceRange | undefined>(undefined)

  useEffect(() => {
    document.title = t('profile.bookings.title')
  }, [])

  const { data: serviceTypes } = useClientLocationServiceType(accountName)

  const serviceTypeIdByName = useMemo(() => {
    const byName = new Map<string, string>()

    serviceTypes?.results?.forEach((lst) => {
      if (lst.serviceType?.name && lst.serviceType.id) {
        byName.set(lst.serviceType.name, lst.serviceType.id)
      }
    })

    return byName
  }, [serviceTypes?.results])

  const serviceTypeId = serviceType === ALL ? undefined : serviceTypeIdByName.get(serviceType)
  const statusOption = STATUS_FILTER_OPTIONS.find((option) => option.value === status)

  const filters = useMemo(
    () => ({
      invoiceSubStatus: statusOption?.subStatuses,
      'locationServiceType.serviceTypeId': serviceTypeId,
    }),
    [statusOption, serviceTypeId]
  )

  const { invoices, isLoading, hasMore, loadMore } = useBookingInvoices(accountName, {
    view,
    filters,
    range: visibleRange,
    sortOrder,
    limit: rowsPerPage,
  })

  const onVisibleRangeChange = useCallback(
    (start: Date, end: Date, focusDate?: Date) => {
      setVisibleRange(toRange(start, end))
      if (focusDate) setMonth(format(focusDate, 'yyyy-MM'))
    },
    [setMonth]
  )

  const onSort = useCallback(
    (sortColumn: string, nextSortOrder: 'asc' | 'desc') => setSort(sortColumn, nextSortOrder),
    [setSort]
  )

  const focusYear = monthDate.getFullYear()
  const focusMonth = monthDate.getMonth()
  const currentYear = new Date().getFullYear()
  const firstYear = Math.min(currentYear - 5, focusYear)
  const yearOptions = Array.from(
    { length: Math.max(currentYear + 5, focusYear) - firstYear + 1 },
    (_, i) => firstYear + i
  )
  const showMonthOf = (year: number, month: number) => setMonth(format(new Date(year, month, 1), 'yyyy-MM'))

  const filterSelects = (fullWidth: boolean) => (
    <>
      <Select
        value={status}
        size="small"
        fullWidth={fullWidth}
        onChange={(e) => setStatus(e.target.value)}
        sx={fullWidth ? undefined : { minWidth: 150, maxWidth: 180 }}
        aria-label={t('calendar.filter.status')}
      >
        <MenuItem value={ALL}>{t('calendar.filter.allStatuses')}</MenuItem>
        {STATUS_FILTER_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {td(option.labelKey)}
          </MenuItem>
        ))}
      </Select>

      <Select
        value={serviceType}
        size="small"
        fullWidth={fullWidth}
        onChange={(e) => setServiceType(e.target.value)}
        sx={fullWidth ? undefined : { minWidth: 150, maxWidth: 180 }}
        aria-label={t('calendar.filter.serviceType')}
      >
        <MenuItem value={ALL}>{t('calendar.filter.allServiceTypes')}</MenuItem>
        {SERVICE_TYPE_FILTER_OPTIONS.map((option) => (
          <MenuItem key={option} value={option}>
            {getServiceTypeLabel(option)}
          </MenuItem>
        ))}
      </Select>
    </>
  )

  const filterButton = (
    <IconButton aria-label={t('calendar.filters')} onClick={() => setFiltersOpen(true)}>
      <FilterListIcon />
    </IconButton>
  )

  return (
    <Box>
      <Notification lupId={sessionCtx.state.user?.lupId || ''} />

      <Stack direction="row" alignItems="center" justifyContent="space-between" px={isBelowMd ? 2 : 0} mb={2}>
        <Typography variant="h2">{t('profile.bookings.title')}</Typography>

        <ToggleButtonGroup
          value={view}
          exclusive
          size={isBelowMd ? 'small' : 'medium'}
          onChange={(_, nextView: BookingsView | null) => {
            if (nextView) setView(nextView)
          }}
          aria-label={t('profile.bookings.viewToggle', {
            viewName: view === 'calendar' ? 'list' : 'calendar',
          })}
        >
          <ToggleButton value="list" aria-label={t('profile.bookings.listView')} sx={{ minWidth: '100px' }}>
            {t('profile.bookings.listView')}
          </ToggleButton>

          <ToggleButton value="calendar" aria-label={t('profile.bookings.calendarView')} sx={{ minWidth: '100px' }}>
            {t('profile.bookings.calendarView')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {!isBelowMd && (
        <Stack direction={isBelowLg ? 'column' : 'row'} justifyContent="space-between" mb={2}>
          <Stack direction="row" gap={2}>
            {filterSelects(false)}
          </Stack>

          <Box mt={2} sx={{ display: { xs: 'none', md: 'block' } }}>
            {view === 'calendar' && <BookingCalendarLegend />}
          </Box>
        </Stack>
      )}

      {view === 'list' && isBelowMd && (
        <Stack direction="row" justifyContent="flex-end" px={2} mb={1}>
          {filterButton}
        </Stack>
      )}

      {view === 'list' && isLoading && invoices.length === 0 ? (
        <Skelly />
      ) : (
        <Box sx={{ pb: '130px', margin: '0 auto' }}>
          {view === 'list' ? (
            <BookingList
              accountName={accountName}
              invoices={invoices}
              isLoading={isLoading && invoices.length === 0}
              hasMore={hasMore}
              loadMore={loadMore}
              onSort={onSort}
              onRowsPerPageChange={setRowsPerPage}
              defaultSortColumn={sortBy}
              defaultSortOrder={sortOrder}
              initialRowsPerPage={rowsPerPage}
            />
          ) : (
            <Paper sx={{ px: 0.75, pt: 1, pb: 1.75 }}>
              <BookingCalendar
                accountName={accountName}
                invoices={invoices}
                initialDate={monthDate}
                date={monthDate}
                headerAction={isBelowMd ? filterButton : undefined}
                onVisibleRangeChange={onVisibleRangeChange}
              />
            </Paper>
          )}
        </Box>
      )}

      <Drawer
        anchor="bottom"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '70vh' } }}
      >
        <Box p={2} pb={4}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">{t('calendar.filters')}</Typography>
            <IconButton aria-label={t('calendar.close')} onClick={() => setFiltersOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Stack spacing={2}>
            {view === 'calendar' && (
              <Stack direction="row" gap={2}>
                <Select
                  value={focusMonth}
                  size="small"
                  fullWidth
                  onChange={(e) => showMonthOf(focusYear, Number(e.target.value))}
                  aria-label={t('calendar.month')}
                  sx={{ flex: 1 }}
                >
                  {MONTH_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>

                <Select
                  value={focusYear}
                  size="small"
                  fullWidth
                  onChange={(e) => showMonthOf(Number(e.target.value), focusMonth)}
                  aria-label={t('calendar.year')}
                  sx={{ flex: 1 }}
                >
                  {yearOptions.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
            )}

            {filterSelects(true)}
          </Stack>
        </Box>
      </Drawer>
    </Box>
  )
}
